import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Card } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { getPrimaryColor } from '@modules/theme';
import SurveyQuestion from './SurveyQuestion';
import { useQuestionExpandState } from 'interviewEditor/QuestionExpandContext';
import { Question } from '@synergetx/puppet-interview-types';

const COLLAPSED_MAX_HEIGHT = 130;

interface SortableQuestionCardProps {
    id: string;
    question: Question;
    position: number;
    isLastQuestion: boolean;
    hasError: boolean;
}

export default function SortableQuestionCard({
    id,
    question,
    position,
    isLastQuestion,
    hasError,
}: SortableQuestionCardProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
    });

    const { isExpanded } = useQuestionExpandState(position);
    const isCollapsed = !isExpanded;

    const cardStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        borderLeft: isExpanded ? 4 : 0,
        borderLeftColor: getPrimaryColor(),
        borderRadius: '8px',
        position: 'relative',
        overflow: 'hidden',
        ...(isCollapsed && {
            maxHeight: COLLAPSED_MAX_HEIGHT,
        }),
        '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        },
        '&:hover .drag-handle': {
            opacity: 1,
        },
        ...(hasError && {
            border: 2,
            borderColor: 'rgba(255, 0, 19, 0.7)',
        }),
    } as const;

    return (
        <Card ref={setNodeRef} sx={cardStyle} {...attributes} id={`${question.position}`}>
            <Box
                className="drag-handle"
                {...listeners}
                sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.7,
                    transition: 'all 0.2s ease',
                    color: 'grey.600',
                    backgroundColor: 'grey.50',
                    borderRight: '1px solid',
                    borderRightColor: 'grey.200',
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                    zIndex: 2,
                    cursor: isDragging ? 'grabbing' : 'grab',
                    '&:hover': {
                        color: 'grey.800',
                        backgroundColor: 'grey.100',
                        opacity: 1,
                    },
                }}
            >
                <DragIndicatorIcon />
            </Box>
            <SurveyQuestion
                isLastQuestion={isLastQuestion}
                position={position}
                question={question}
            />
            {isCollapsed && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 36,
                        right: 0,
                        height: 30,
                        background:
                            'linear-gradient(transparent, rgba(255,255,255,0.9) 60%, rgba(255,255,255,1))',
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                />
            )}
        </Card>
    );
}
